import React, { useRef, useEffect, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { Mesh, Vector3, Euler, Group, AnimationMixer, AnimationClip, AnimationAction, LoopOnce, Color } from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'

interface OtherPlayerRobotProps {
  playerId: string
  position: [number, number, number]
  rotation: [number, number, number]
  velocity?: { x: number, y: number, z: number }
  timestamp?: number
}

const OtherPlayerRobot: React.FC<OtherPlayerRobotProps> = ({
  playerId,
  position,
  rotation,
  velocity = { x: 0, y: 0, z: 0 },
  timestamp = 0
}) => {
  const groupRef = useRef<Group>(null)
  const lastUpdateTime = useRef(Date.now())
  const lastPosition = useRef<Vector3>(new Vector3(...position))
  const targetPosition = useRef<Vector3>(new Vector3(...position))
  const targetRotation = useRef<Euler>(new Euler(...rotation))

  // Estado para o modelo GLTF
  const [model, setModel] = useState<Group | null>(null)
  const [mixer, setMixer] = useState<AnimationMixer | null>(null)
  const [actions, setActions] = useState<Record<string, AnimationAction>>({})
  const [currentAction, setCurrentAction] = useState<string>('Idle')

  // Carregar o modelo GLTF
  useEffect(() => {
    const loader = new GLTFLoader()
    loader.load('/models/gltf/RobotExpressive/RobotExpressive.glb', (gltf) => {
      const robotModel = gltf.scene.clone()

      // Ajustar escala e posição - reduzido para ficar mais proporcional ao Spaceship
      // Diminuir o tamanho do robô pela metade (era 0.7)
      robotModel.scale.set(0.35, 0.35, 0.35)
      robotModel.position.set(...position)
      robotModel.rotation.set(...rotation)

      // Modificar as cores do robô conforme solicitado - textura metálica prateada com aspecto alienígena
      console.log('Modificando cores do OtherPlayerRobot - Textura metálica prateada com aspecto alienígena');

      // Cores a serem aplicadas
      const BLACK = '#000000';       // Preto puro
      const DARK_GRAY = '#1A1A1A';   // Cinza escuro (quase preto)
      const MEDIUM_GRAY = '#444444'; // Cinza médio
      const LIGHT_GRAY = '#777777';  // Cinza claro
      const SILVER = '#C0C0C0';      // Prateado
      const CHROME = '#D3D3D3';      // Prateado mais claro (tipo cromo)
      const PLATINUM = '#E5E4E2';    // Platina (prateado brilhante)
      const WHITE = '#FFFFFF';       // Branco

      // Tons metálicos com aspecto alienígena
      const ALIEN_SILVER = '#A8A9AD'; // Prateado com tom alienígena
      const STEEL_BLUE = '#4682B4';   // Azul metálico
      const ALIEN_GLOW = '#7DF9FF';   // Azul elétrico brilhante (tipo glow alien)
      const GUNMETAL = '#2C3539';      // Metal escuro (tipo arma)

      // Mapeamento de partes para cores específicas - invertido (claro -> escuro, escuro -> claro)
      const partColors = {
        // Cabeça e face
        'head': GUNMETAL,        // Era ALIEN_SILVER (claro), agora escuro
        'face': DARK_GRAY,       // Era SILVER (claro), agora escuro
        'eye': BLACK,           // Era ALIEN_GLOW (claro/brilhante), agora escuro
        'eyebrow': BLACK,       // Alterado para preto para manter consistência com o robô local
        'mouth': CHROME,        // Era GUNMETAL (escuro), agora claro
        'antenna': BLACK,       // Era ALIEN_GLOW (claro/brilhante), agora escuro

        // Corpo
        'body': GUNMETAL,       // Era SILVER (claro), agora escuro
        'torso': DARK_GRAY,     // Era ALIEN_SILVER (claro), agora escuro
        'chest': DARK_GRAY,     // Era CHROME (claro), agora escuro
        'shoulder': SILVER,     // Era GUNMETAL (escuro), agora claro

        // Braços
        'arm': GUNMETAL,        // Era SILVER (claro), agora escuro
        'hand': CHROME,         // Era GUNMETAL (escuro), agora claro
        'finger': DARK_GRAY,    // Era CHROME (claro), agora escuro
        'elbow': SILVER,        // Era DARK_GRAY (escuro), agora claro
        'wrist': DARK_GRAY,     // Era STEEL_BLUE (médio), agora escuro

        // Pernas
        'leg': GUNMETAL,        // Era SILVER (claro), agora escuro
        'foot': CHROME,         // Era GUNMETAL (escuro), agora claro
        'toe': DARK_GRAY,       // Era CHROME (claro), agora escuro
        'knee': SILVER,         // Era DARK_GRAY (escuro), agora claro
        'ankle': DARK_GRAY,     // Era STEEL_BLUE (médio), agora escuro

        // Detalhes
        'joint': SILVER,        // Era DARK_GRAY (escuro), agora claro
        'button': BLACK,        // Era ALIEN_GLOW (claro/brilhante), agora escuro
        'detail': DARK_GRAY,    // Era STEEL_BLUE (médio), agora escuro
        'accent': BLACK,        // Era ALIEN_GLOW (claro/brilhante), agora escuro
        'panel': CHROME,        // Era GUNMETAL (escuro), agora claro
        'trim': DARK_GRAY,      // Era CHROME (claro), agora escuro
      };

      // Função para determinar a cor com base no nome da parte - invertido
      const getColorForPart = (partName) => {
        const name = partName.toLowerCase();

        // Verificar correspondências exatas primeiro
        for (const [key, color] of Object.entries(partColors)) {
          if (name === key) {
            return color;
          }
        }

        // Verificar correspondências parciais
        for (const [key, color] of Object.entries(partColors)) {
          if (name.includes(key)) {
            return color;
          }
        }

        // Cores padrão com base em padrões comuns - invertido
        if (name.includes('yellow')) return SILVER;     // Era GUNMETAL (escuro), agora claro
        if (name.includes('white')) return DARK_GRAY;   // Era SILVER (claro), agora escuro
        if (name.includes('black')) return WHITE;       // Era BLACK (escuro), agora claro
        if (name.includes('metal')) return GUNMETAL;    // Era ALIEN_SILVER (claro), agora escuro
        if (name.includes('joint')) return SILVER;      // Era DARK_GRAY (escuro), agora claro

        // Padrão para partes não identificadas - invertido
        return GUNMETAL;                                // Era SILVER (claro), agora escuro
      };

      // Função para aplicar cores a um objeto
      const applyColorToObject = (object, color) => {
        if (object && object.isMesh && object.material) {
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach(material => {
            material.color.set(color);
            material.needsUpdate = true;
          });
          console.log(`Cor ${color} aplicada a: ${object.name}`);
        }
      };

      // Aplicar cores a todas as partes do modelo
      console.log('Aplicando cores específicas a cada parte do OtherPlayerRobot...');

      // Primeiro, vamos identificar e colorir partes específicas
      const coloredParts = new Set(); // Rastrear partes já coloridas

      // 1. Olhos e sobrancelhas sempre pretos (para manter consistência com o robô local)
      robotModel.traverse((object) => {
        const name = object.name.toLowerCase();
        if (name.includes('eye') || name.includes('eyebrow')) {
          applyColorToObject(object, BLACK);
          coloredParts.add(object.uuid);
          console.log(`Olho/sobrancelha colorido de preto: ${object.name}`);
        }
      });

      // 2. Corpo principal em escuro com detalhes claros (invertido)
      const body = robotModel.getObjectByName('Body');
      if (body) {
        applyColorToObject(body, GUNMETAL);
        coloredParts.add(body.uuid);
        console.log(`Corpo principal colorido de gunmetal (escuro): ${body.name}`);

        // Adicionar detalhes ao corpo - invertido
        body.traverse((part) => {
          if (part !== body && part.isMesh && !coloredParts.has(part.uuid)) {
            const name = part.name.toLowerCase();
            // Articulações e juntas em prateado (claro)
            if (name.includes('joint') || name.includes('connector')) {
              applyColorToObject(part, SILVER);
              coloredParts.add(part.uuid);
            }
            // Detalhes do peito em cinza escuro
            else if (name.includes('chest') || name.includes('front')) {
              applyColorToObject(part, DARK_GRAY);
              coloredParts.add(part.uuid);
            }
            // Detalhes laterais em cromo (claro)
            else if (name.includes('side') || name.includes('panel')) {
              applyColorToObject(part, CHROME);
              coloredParts.add(part.uuid);
            }
            // Botões ou controles em preto
            else if (name.includes('button') || name.includes('control')) {
              applyColorToObject(part, BLACK);
              coloredParts.add(part.uuid);
            }
          }
        });
      }

      // 3. Braços e pernas com padrão invertido (escuro onde era claro, claro onde era escuro)
      robotModel.traverse((object) => {
        if (object.isMesh && !coloredParts.has(object.uuid)) {
          const name = object.name.toLowerCase();

          // Braços: base escura, detalhes claros, mãos em cromo
          if (name.includes('arm')) {
            if (name.includes('upper')) {
              applyColorToObject(object, GUNMETAL);       // Era SILVER (claro), agora escuro
            } else if (name.includes('lower')) {
              applyColorToObject(object, DARK_GRAY);       // Era ALIEN_SILVER (claro), agora escuro
            } else if (name.includes('joint') || name.includes('elbow')) {
              applyColorToObject(object, SILVER);         // Era DARK_GRAY (escuro), agora claro
            } else {
              applyColorToObject(object, GUNMETAL);       // Era SILVER (claro), agora escuro
            }
            coloredParts.add(object.uuid);
          }
          else if (name.includes('hand') || name.includes('fist')) {
            applyColorToObject(object, CHROME);           // Era GUNMETAL (escuro), agora claro
            coloredParts.add(object.uuid);
          }
          else if (name.includes('finger') || name.includes('thumb')) {
            applyColorToObject(object, DARK_GRAY);        // Era CHROME (claro), agora escuro
            coloredParts.add(object.uuid);
          }
          else if (name.includes('wrist')) {
            applyColorToObject(object, DARK_GRAY);         // Era STEEL_BLUE (médio), agora escuro
            coloredParts.add(object.uuid);
          }
          // Pernas: base escura, detalhes claros, pés em cromo
          else if (name.includes('leg')) {
            if (name.includes('upper')) {
              applyColorToObject(object, GUNMETAL);       // Era SILVER (claro), agora escuro
            } else if (name.includes('lower')) {
              applyColorToObject(object, DARK_GRAY);       // Era ALIEN_SILVER (claro), agora escuro
            } else if (name.includes('joint') || name.includes('knee')) {
              applyColorToObject(object, SILVER);         // Era DARK_GRAY (escuro), agora claro
            } else {
              applyColorToObject(object, GUNMETAL);       // Era SILVER (claro), agora escuro
            }
            coloredParts.add(object.uuid);
          }
          else if (name.includes('foot') || name.includes('feet')) {
            applyColorToObject(object, CHROME);           // Era GUNMETAL (escuro), agora claro
            coloredParts.add(object.uuid);
          }
          else if (name.includes('toe')) {
            applyColorToObject(object, DARK_GRAY);         // Era CHROME (claro), agora escuro
            coloredParts.add(object.uuid);
          }
          else if (name.includes('ankle')) {
            applyColorToObject(object, DARK_GRAY);         // Era STEEL_BLUE (médio), agora escuro
            coloredParts.add(object.uuid);
          }
        }
      });

      // 4. Partes brancas originais agora em cinza escuro (invertido)
      robotModel.traverse((object) => {
        if (object.isMesh && !coloredParts.has(object.uuid)) {
          const materials = Array.isArray(object.material) ? object.material : [object.material];

          materials.forEach(material => {
            const color = material.color;
            // Identificar partes brancas pela cor
            if (color.r > 0.8 && color.g > 0.8 && color.b > 0.8) {
              material.color.set(DARK_GRAY);                // Era SILVER (claro), agora escuro
              material.needsUpdate = true;
              coloredParts.add(object.uuid);
              console.log(`Parte branca alterada para cinza escuro: ${object.name}`);
            }
          });
        }
      });

      // 5. Partes amarelas originais agora em prateado (invertido)
      robotModel.traverse((object) => {
        if (object.isMesh && !coloredParts.has(object.uuid)) {
          const materials = Array.isArray(object.material) ? object.material : [object.material];

          materials.forEach(material => {
            const color = material.color;
            // Identificar partes amarelas pela cor
            if (color.r > 0.8 && color.g > 0.8 && color.b < 0.3) {
              material.color.set(SILVER);                   // Era GUNMETAL (escuro), agora claro
              material.needsUpdate = true;
              coloredParts.add(object.uuid);
              console.log(`Parte amarela alterada para prateado: ${object.name}`);
            }
          });
        }
      });

      // 6. Colorir partes restantes com base no nome
      robotModel.traverse((object) => {
        if (object.isMesh && !coloredParts.has(object.uuid)) {
          const color = getColorForPart(object.name);
          applyColorToObject(object, color);
          coloredParts.add(object.uuid);
          console.log(`Parte restante colorida com base no nome: ${object.name} -> ${color}`);
        }
      });

      // 7. Adicionar detalhes finais para garantir um visual interessante - invertido
      // Garantir que a cabeça tenha detalhes metálicos e alienígenas
      const head = robotModel.getObjectByName('Head');
      if (head) {
        // Manter a cabeça em gunmetal (escuro)
        applyColorToObject(head, GUNMETAL);                // Era ALIEN_SILVER (claro), agora escuro

        // Adicionar detalhes à cabeça - invertido
        head.traverse((part) => {
          if (part !== head && part.isMesh) {
            const name = part.name.toLowerCase();
            // Olhos em preto
            if (name.includes('eye')) {
              applyColorToObject(part, BLACK);              // Era ALIEN_GLOW (claro), agora escuro
            }
            // Sobrancelhas também em preto (para manter consistência com o robô local)
            else if (name.includes('eyebrow')) {
              applyColorToObject(part, BLACK);             // Alterado para preto para manter consistência
            }
            // Antenas ou detalhes superiores em preto
            else if (name.includes('antenna') || name.includes('top')) {
              applyColorToObject(part, BLACK);            // Era ALIEN_GLOW (claro), agora escuro
            }
            // Boca ou detalhes faciais em cromo (claro)
            else if (name.includes('mouth') || name.includes('face')) {
              applyColorToObject(part, CHROME);           // Era GUNMETAL (escuro), agora claro
            }
            // Detalhes laterais da cabeça em cinza escuro
            else if (name.includes('side') || name.includes('ear')) {
              applyColorToObject(part, DARK_GRAY);        // Era STEEL_BLUE (médio), agora escuro
            }
            // Detalhes traseiros da cabeça em cromo (claro)
            else if (name.includes('back') || name.includes('rear')) {
              applyColorToObject(part, CHROME);           // Era GUNMETAL (escuro), agora claro
            }
            // Outros detalhes em cinza escuro
            else if (name.includes('detail') || name.includes('accent')) {
              applyColorToObject(part, DARK_GRAY);        // Era CHROME (claro), agora escuro
            }
          }
        });
      }

      // 8. Adicionar detalhes metálicos aleatórios para criar uma textura mais interessante - invertido
      // Selecionar algumas partes aleatórias para adicionar detalhes metálicos
      robotModel.traverse((object) => {
        if (object.isMesh && !coloredParts.has(object.uuid)) {
          // Aplicar cores metálicas a algumas partes restantes para criar textura - invertido
          const randomValue = Math.random();
          if (randomValue < 0.2) {
            applyColorToObject(object, SILVER);            // Era GUNMETAL (escuro), agora claro
            console.log(`Detalhe aleatório em prateado: ${object.name}`);
          } else if (randomValue < 0.4) {
            applyColorToObject(object, GUNMETAL);          // Era ALIEN_SILVER (claro), agora escuro
            console.log(`Detalhe aleatório em gunmetal: ${object.name}`);
          } else if (randomValue < 0.5) {
            applyColorToObject(object, DARK_GRAY);         // Era CHROME (claro), agora escuro
            console.log(`Detalhe aleatório em cinza escuro: ${object.name}`);
          } else if (randomValue < 0.55) {
            applyColorToObject(object, BLACK);             // Era ALIEN_GLOW (claro), agora escuro
            console.log(`Detalhe aleatório em preto: ${object.name}`);
          } else {
            applyColorToObject(object, GUNMETAL);         // Era SILVER (claro), agora escuro
            console.log(`Detalhe aleatório em gunmetal: ${object.name}`);
          }
          coloredParts.add(object.uuid);
        }
      });

      console.log('Modificação de cores do OtherPlayerRobot concluída - textura invertida aplicada (claro -> escuro, escuro -> claro)');

      // Configurar mixer e animações
      const newMixer = new AnimationMixer(robotModel)

      // Criar ações para cada animação
      const newActions: Record<string, AnimationAction> = {}
      const states = ['Idle', 'Walking', 'Running', 'Dance', 'Death', 'Sitting', 'Standing']
      const emotes = ['Jump', 'Yes', 'No', 'Wave', 'Punch', 'ThumbsUp']

      for (let i = 0; i < gltf.animations.length; i++) {
        const clip = gltf.animations[i]
        const action = newMixer.clipAction(clip)
        newActions[clip.name] = action

        // Configurar ações de emote para executar apenas uma vez
        if (emotes.indexOf(clip.name) >= 0 || states.indexOf(clip.name) >= 4) {
          action.clampWhenFinished = true
          action.loop = LoopOnce
        }
      }

      // Iniciar com a animação "Idle"
      newActions['Idle'].play()

      // Atualizar estados
      setModel(robotModel)
      setMixer(newMixer)
      setActions(newActions)

      // Adicionar o modelo ao grupo
      if (groupRef.current) {
        groupRef.current.add(robotModel)
      }

      // Retornar uma função de limpeza para remover o modelo quando o componente for desmontado
      return () => {
        console.log('Removendo modelo do OtherPlayerRobot da cena');
        if (groupRef.current && robotModel) {
          // Limpar ao desmontar
          groupRef.current.remove(robotModel);
          // Liberar recursos
          robotModel.traverse((child) => {
            if (child instanceof Mesh) {
              if (child.geometry) child.geometry.dispose();
              if (child.material) {
                if (Array.isArray(child.material)) {
                  child.material.forEach(material => material.dispose());
                } else {
                  child.material.dispose();
                }
              }
            }
          });
        }
      }
    }, undefined, (error) => {
      console.error('Erro ao carregar o modelo do robô remoto:', error)
    })
  }, [])

  // Função para transição suave entre animações
  const fadeToAction = (name: string, duration: number = 0.2) => {
    if (!actions || !actions[name]) return

    const current = actions[currentAction]
    const next = actions[name]

    if (current !== next) {
      current?.fadeOut(duration)
    }

    next
      .reset()
      .setEffectiveTimeScale(1)
      .setEffectiveWeight(1)
      .fadeIn(duration)
      .play()

    setCurrentAction(name)
  }

  // Atualizar posição e rotação quando as props mudarem
  useEffect(() => {
    targetPosition.current = new Vector3(...position)
    targetRotation.current = new Euler(...rotation)
    lastUpdateTime.current = Date.now()

    // Determinar a animação com base na velocidade
    if (model) {
      const speed = new Vector3(velocity.x, 0, velocity.z).length()

      if (speed < 0.1) {
        // Parado
        if (currentAction !== 'Idle') {
          fadeToAction('Idle')
        }
      } else if (speed < 4.0) { // Aumentado para 4.0 (era 2.0) para corresponder à velocidade aumentada do robô local
        // Andando
        if (currentAction !== 'Walking') {
          fadeToAction('Walking')
        }
      } else {
        // Correndo
        if (currentAction !== 'Running') {
          fadeToAction('Running')
        }
      }
    }
  }, [position, rotation, velocity])

  // Forçar atualização de posição periodicamente
  useEffect(() => {
    const interval = setInterval(() => {
      if (model) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Forced position update for player ${playerId}`)
        }
        model.position.copy(targetPosition.current)
        model.rotation.copy(targetRotation.current)
      }
    }, 5000)

    return () => clearInterval(interval)
  }, [model, playerId])

  // Interpolação suave da posição e rotação
  useFrame((state, delta) => {
    if (model) {
      // Atualizar o mixer de animação
      if (mixer) {
        mixer.update(delta)
      }

      // Calcular o fator de interpolação
      const now = Date.now()
      const elapsed = now - lastUpdateTime.current
      const interpolationFactor = Math.min(elapsed / 100, 1) // Interpolação em 100ms

      // Interpolar posição
      model.position.lerp(targetPosition.current, interpolationFactor)

      // Interpolar rotação (mais complexo para evitar problemas com ângulos)
      const currentRotation = model.rotation
      currentRotation.x += (targetRotation.current.x - currentRotation.x) * interpolationFactor
      currentRotation.y += (targetRotation.current.y - currentRotation.y) * interpolationFactor
      currentRotation.z += (targetRotation.current.z - currentRotation.z) * interpolationFactor

      // Atualizar última posição
      lastPosition.current.copy(model.position)
    }
  })

  return <group ref={groupRef} />
}

export default OtherPlayerRobot
